import { useState, useEffect } from 'react';
import { NavigateFunction } from 'react-router-dom';

interface Props {
  lang: 'zh' | 'en';
  navigate: NavigateFunction;
}

const SLIDES = [
  {
    img: 'https://public.readdy.ai/ai/img_res/e5807b88f7dbe716a17ed872075e610c.jpg',
    zh: { title: '购未来', sub: '在星际', desc: '全球首个星际元宇宙商城 · 500+未来商品 · 20大品类分区' },
    en: { title: 'Shop the Future', sub: 'Among the Stars', desc: "Earth's First Interstellar Metaverse Mall · 500+ Future Products · 20 Category Zones" },
    color: '#FFD700',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=VR%20AR%20immersive%20shopping%20experience%20holographic%20product%20display%20futuristic%20store%20interior%20neon%20glow%20dark%20background%20sci-fi%20cinematic%20ultra%20detailed%208k%20wide%20luxury%20retail%20technology&width=1920&height=1080&seq=mall-hero-s2-v3&orientation=landscape',
    zh: { title: 'VR·AR·裸眼3D', sub: '沉浸购物', desc: '突破屏幕边界 · 触摸未来商品 · 全感官沉浸体验' },
    en: { title: 'VR · AR · Naked-Eye 3D', sub: 'Immersive Shopping', desc: 'Break the Screen · Touch Tomorrow\'s Products · Full-Sensory Immersion' },
    color: '#FF6B9D',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=humanoid%20robots%20space%20travel%20quantum%20computers%20futuristic%20products%20showcase%20dark%20background%20neon%20glow%20sci-fi%20technology%20exhibition%20ultra%20detailed%208k%20cinematic%20wide&width=1920&height=1080&seq=mall-hero-s3-v3&orientation=landscape',
    zh: { title: '机器人·太空·量子', sub: '未来商品专区', desc: '人形机器人 · 太空旅行票 · 量子计算机 · 脑机接口' },
    en: { title: 'Robots · Space · Quantum', sub: 'Future Product Zones', desc: 'Humanoid Robots · Space Travel · Quantum Computers · BCI Devices' },
    color: '#00FFCC',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=drone%20delivery%20futuristic%20city%20night%20neon%20lights%20autonomous%20delivery%20flying%20package%20urban%20sci-fi%20dark%20background%20cinematic%20ultra%20detailed%208k%20wide%20balloon%20logistics&width=1920&height=1080&seq=mall-hero-s4-v3&orientation=landscape',
    zh: { title: '无人机直送', sub: 'AETHER GLOBE', desc: '星际物流 · 重新定义配送体验 · 全球48小时到达' },
    en: { title: 'Drone Direct', sub: 'AETHER GLOBE', desc: 'Interstellar Logistics · Redefining Delivery · 48h Global Arrival' },
    color: '#A855F7',
  },
];

export default function MallHeroSection({ lang, navigate }: Props) {
  const [slide, setSlide] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
    const t = setInterval(() => setSlide(s => (s + 1) % SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  const s = SLIDES[slide];

  return (
    <section className="relative overflow-hidden" style={{ height: '100vh', minHeight: '700px' }}>
      {SLIDES.map((sl, i) => (
        <img key={i} src={sl.img} alt="" className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-1000" style={{ opacity: slide === i ? 1 : 0 }} />
      ))}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(1,2,8,0.4) 0%, rgba(1,2,8,0.55) 40%, rgba(1,2,8,0.97) 100%)' }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(255,200,50,0.06) 0%, transparent 60%)' }} />

      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        {/* Logo Badge */}
        <div className="mb-6" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(-30px)', transition: 'all 0.8s ease' }}>
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full" style={{ background: 'rgba(255,215,0,0.1)', border: '1.5px solid rgba(255,215,0,0.4)', backdropFilter: 'blur(10px)' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFD700, #FF6B35)' }}>
              <i className="ri-store-3-line text-sm text-black" />
            </div>
            <span className="font-black text-xs tracking-widest" style={{ fontFamily: 'Orbitron, sans-serif', color: '#FFD700' }}>NEXUS OMNI MALL</span>
            <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(255,64,129,0.2)', color: '#FF4081', border: '1px solid rgba(255,64,129,0.4)' }}>{lang === 'zh' ? '星际元宇宙商城' : 'Interstellar Metaverse'}</span>
          </div>
        </div>

        {/* Main Title */}
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.9s ease 0.2s' }}>
          <h1 className="font-black text-white leading-none mb-2" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 'clamp(3rem, 8vw, 7rem)' }}>
            <span style={{ color: s.color }}>{s[lang].title}</span>
          </h1>
          <h2 className="font-black text-white leading-none mb-5" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 'clamp(2rem, 5vw, 4.5rem)', opacity: 0.85 }}>
            {s[lang].sub}
          </h2>
          <p className="text-lg md:text-xl mb-8 font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>{s[lang].desc}</p>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 mb-12" style={{ opacity: visible ? 1 : 0, transition: 'all 1s ease 0.4s' }}>
          <a href="#zones" className="flex items-center gap-3 px-8 py-4 rounded-2xl font-black text-sm cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #FFD700, #FF6B35)', color: '#000', fontFamily: 'Orbitron, sans-serif', boxShadow: '0 0 40px rgba(255,215,0,0.4)' }}>
            <i className="ri-store-3-line" />{lang === 'zh' ? '探索20大分区' : 'EXPLORE 20 ZONES'}
          </a>
          <a href="#guide" className="flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-sm cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(255,255,255,0.2)', color: 'white', backdropFilter: 'blur(10px)' }}>
            <i className="ri-compass-3-line" />{lang === 'zh' ? '购物指引' : 'Shopping Guide'}
          </a>
          <a href="#space" className="flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-sm cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: 'rgba(255,64,129,0.15)', border: '1.5px solid rgba(255,64,129,0.4)', color: '#FF4081' }}>
            <i className="ri-moon-line" />{lang === 'zh' ? '月球/火星实况' : 'Moon/Mars Live'}
          </a>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap justify-center gap-8" style={{ opacity: visible ? 1 : 0, transition: 'all 1.1s ease 0.6s' }}>
          {[
            { v: '20+', l: lang === 'zh' ? '商品分区' : 'Product Zones', i: 'ri-grid-line', c: '#FFD700' },
            { v: '500+', l: lang === 'zh' ? '未来商品' : 'Future Products', i: 'ri-store-3-line', c: '#FF6B9D' },
            { v: '8D', l: lang === 'zh' ? '沉浸体验' : 'Immersive XP', i: 'ri-glasses-2-line', c: '#00FFCC' },
            { v: '∞', l: lang === 'zh' ? '星际物流' : 'Interstellar Delivery', i: 'ri-rocket-line', c: '#FF6B35' },
            { v: '100%', l: lang === 'zh' ? '量子加密' : 'Quantum Encrypted', i: 'ri-shield-check-line', c: '#A855F7' },
          ].map((st, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${st.c}15`, border: `1px solid ${st.c}30` }}>
                <i className={`${st.i} text-sm`} style={{ color: st.c }} />
              </div>
              <div>
                <div className="font-black text-xl" style={{ color: st.c, fontFamily: 'Orbitron, sans-serif' }}>{st.v}</div>
                <div className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{st.l}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Slide Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {SLIDES.map((_, i) => (
          <button key={i} onClick={() => setSlide(i)} className="cursor-pointer transition-all duration-300" style={{ width: slide === i ? '32px' : '8px', height: '8px', borderRadius: '4px', background: slide === i ? '#FFD700' : 'rgba(255,255,255,0.25)' }} />
        ))}
      </div>
    </section>
  );
}
